import { createModalStoreFactory } from '@/features/_shared/stores/create-modal.store';
import type {
  ICreateSpaceModalProps,
  TCreateSpaceModalType,
} from '../../../spaces/components/modals/create-space-modal/create-space-modal';
import type {
  IDeleteSpaceModalProps,
  TDeleteSpaceModalType,
} from '../../../spaces/components/modals/delete-space-modal/delete-space-modal';
import type { IShareSpaceModalProps, TShareSpaceModalType } from '../../../spaces/components/modals/share-space-modal';
import type {
  IUpdateSpaceModalProps,
  TUpdateSpaceModalType,
} from '../../../spaces/components/modals/update-space-modal/update-space-modal';
import type {
  IDeleteLabelTicketModalProps,
  TDeleteLabelTicketModalType,
} from '../../../label-tickets/components/modals/delete-label-ticket-modal';
import type {
  ICreateLabelTicketModalProps,
  TCreateLabelTicketModalType,
} from '../../../label-tickets/components/modals/create-label-ticket-modal';
import type {
  IUpdateLabelTicketModalProps,
  TUpdateLabelTicketModalType,
} from '../../../label-tickets/components/modals/update-label-ticket-modal';
import type {
  IDeleteUserTicketingModalProps,
  TDeleteUserTicketingModalType,
} from '../../../user-ticketing/components/modals/delete-user-ticketing-modal';
import type {
  IInviteAsAdminModalProps,
  TInviteAsAdminModalType,
} from '../../../ticketing-invitations/components/invite-as-admin-modal';
import type {
  IDeleteInvitationModalProps,
  TDeleteInvitationModalType,
} from '../../../ticketing-invitations/components/delete-invitation-modal';
import type {
  IInvitationModalProps,
  TInvitationModalType,
} from '../../../ticketing-invitations/components/invitation-modal';
import type {
  TCreateColumnModalProps,
  TCreateColumnModalType,
} from '../../../columns/components/modals/create-column-modal';
import type {
  TUpdateColumnModalProps,
  TUpdateColumnModalType,
} from '../../../columns/components/modals/update-column-modal';

import type {
  ICreateTicketModalProps,
  TCreateTicketModalType,
} from '../../../tickets/components/modals/create-ticket-modal';
import type {
  IUpdateTicketModalProps,
  TUpdateTicketModalType,
} from '../../../tickets/components/modals/update-ticket-modal';
import type {
  IDuplicateTicketModalProps,
  TDuplicateTicketModalType,
} from '../../../tickets/components/modals/duplicate-ticket-modal/duplicate-ticket-modal';
import { useShallow } from 'zustand/react/shallow';
import type {
  IDeleteSpaceMemberModalProps,
  TDeleteSpaceMemberModalType,
} from '../../../space-members/components/modals/delete-space-member-modal';
import type {
  ISetAsAdminUserTicketingModalProps,
  TSetAsAdminUserTicketingModalType,
} from '../../../user-ticketing/components/modals/set-as-admin-user-ticketing-modal';
import type {
  IFilterTicketModalProps,
  TFilterTicketModalType,
} from '../../../tickets/components/modals/filter-ticket-modal/filter-ticket-modal';
import type {
  ICompleteTicketModalProps,
  TCompleteTicketModalType,
} from '../../../tickets/components/modals/complete-ticket-modal/complete-ticket-modal';
import type {
  IDeleteCommentModalProps,
  TDeleteCommentModalType,
} from '../../../ticket-comments/components/modals/delete-comment-modal/delete-comment-modal';
import {
  IDeleteColumnModalProps,
  TDeleteColumnModalType,
} from '@/features/ticketing/columns/components/modals/delete-column-modal/delete-column-modal';
import {
  IDeleteTicketModalProps,
  TDeleteTicketModalType,
} from '@/features/ticketing/tickets/components/modals/delete-ticket-modal/delete-ticket-modal';
import {
  ICloseConfirmationTicketModalProps,
  TCloseConfirmationTicketModalType,
} from '@/features/ticketing/tickets/components/modals/close-confirmation-ticket-modal/close-confirmation-ticket-modal';
import {
  IConfirmationCloseTicketCommentModalProps,
  TConfirmationCloseTicketCommentModalType,
} from '@/features/ticketing/ticket-comments/components/modals/confirmation-close-ticket-comment-modal/confirmation-close-ticket-comment';
import type {
  IConfirmationMarkAsCompleteTicketModalProps,
  TConfirmationMarkAsCompleteTicketModalType,
} from '@/features/ticketing/tickets/components/modals/confirmation-mark-as-complete-ticket-modal/confirmation-mark-as-complete-ticket-modal';

import type {
  TRelatedTicketModalType,
  IRelatedTicketModalProps,
} from '@/features/ticketing/tickets/components/modals/related-ticket-modal/related-ticket-modal';

// --------- Space Modal Types ---------
export type TSpaceModalTypes =
  | TCreateSpaceModalType
  | TUpdateSpaceModalType
  | TDeleteSpaceModalType
  | TShareSpaceModalType;

// --------- Space Member Modal Types ---------
export type TSpaceMemberModalTypes = TDeleteSpaceMemberModalType;

// --------- Label Ticket Modal Types ---------
export type TLabelTicketModalTypes =
  | TCreateLabelTicketModalType
  | TUpdateLabelTicketModalType
  | TDeleteLabelTicketModalType;

// --------- User Ticketing Modal Types ---------
export type TUserTicketingModalTypes = TDeleteUserTicketingModalType | TSetAsAdminUserTicketingModalType;

// --------- Ticketing Invitation Modal Types ---------
export type TTicketingInvitationModalTypes = TInviteAsAdminModalType;

// --------- Ticketing Column Modal Types ---------
export type TTicketingColumnModalTypes = TCreateColumnModalType | TUpdateColumnModalType | TDeleteColumnModalType;

// --------- Ticketing Ticket Modal Types ---------
export type TTicketingTicketModalTypes =
  | TDeleteTicketModalType
  | TCompleteTicketModalType
  | TCreateTicketModalType
  | TUpdateTicketModalType
  | TDuplicateTicketModalType
  | TCloseConfirmationTicketModalType
  | TConfirmationMarkAsCompleteTicketModalType;

// --------- Invitation Modal Types ---------
export type TInvitationModalTypes = TInvitationModalType | TDeleteInvitationModalType;

// --------- Filter Ticket Modal Types ---------
export type TFilterTicketModalTypes = TFilterTicketModalType;

// --------- Ticket Comment Modal Types ---------
export type TTicketCommentModalTypes = TDeleteCommentModalType | TConfirmationCloseTicketCommentModalType;

// --------- Ticketing Modal Types ---------
export type TTicketingModalTypes =
  | TSpaceModalTypes
  | TSpaceMemberModalTypes
  | TLabelTicketModalTypes
  | TUserTicketingModalTypes
  | TTicketingInvitationModalTypes
  | TInvitationModalTypes
  | TTicketingColumnModalTypes
  | TTicketingTicketModalTypes
  | TFilterTicketModalTypes
  | TTicketCommentModalTypes
  | TRelatedTicketModalType;
// --------- Ticketing Modal Props ---------
interface TTicketingModalProps {
  // Space Modal
  CreateSpaceModal: ICreateSpaceModalProps;
  DeleteSpaceModal: IDeleteSpaceModalProps;
  ShareSpaceModal: IShareSpaceModalProps;
  UpdateSpaceModal: IUpdateSpaceModalProps;

  // Space Member Modal
  DeleteSpaceMemberModal: IDeleteSpaceMemberModalProps;

  // Label Ticket Modal
  CreateLabelTicketModal: ICreateLabelTicketModalProps;
  UpdateLabelTicketModal: IUpdateLabelTicketModalProps;
  DeleteLabelTicketModal: IDeleteLabelTicketModalProps;

  // User Ticketing Modal
  DeleteUserTicketingModal: IDeleteUserTicketingModalProps;
  SetAsAdminUserTicketingModal: ISetAsAdminUserTicketingModalProps;

  // Ticketing Invitation Modal
  InviteAsAdminModal: IInviteAsAdminModalProps;

  // Delete Invitation Modal
  DeleteInvitationModal: IDeleteInvitationModalProps;
  InvitationModal: IInvitationModalProps;

  // Ticketing Column Modal
  CreateColumnModal: TCreateColumnModalProps;
  UpdateColumnModal: TUpdateColumnModalProps;
  DeleteColumnModal: IDeleteColumnModalProps;

  // Ticketing Ticket Modal
  DeleteTicketModal: IDeleteTicketModalProps;
  CreateTicketModal: ICreateTicketModalProps;
  UpdateTicketModal: IUpdateTicketModalProps;
  DuplicateTicketModal: IDuplicateTicketModalProps;
  CompleteTicketModal: ICompleteTicketModalProps;
  CloseConfirmationTicketModal: ICloseConfirmationTicketModalProps;
  ConfirmationMarkAsCompleteTicketModal: IConfirmationMarkAsCompleteTicketModalProps;
  RelatedTicketModal: IRelatedTicketModalProps;

  // Filter Ticket Modal
  FilterTicketModal: IFilterTicketModalProps;

  // Ticket Comment Modal
  DeleteCommentModal: IDeleteCommentModalProps;
  ConfirmationCloseTicketCommentModal: IConfirmationCloseTicketCommentModalProps;
}

export const useTicketingModalStore = createModalStoreFactory<TTicketingModalTypes, TTicketingModalProps>();

export const useTicketingModalActions = () => useTicketingModalStore(useShallow((state) => state.actions));
